<template>
  <div>
    <b-modal
      :visible="isVisible"
      size="xl"
      @hidden="closeModal"
    >
      <template #modal-title>
        {{ $t('traflMqayis') }}
      </template>

      <div>
        <b-button @click.prevent="resetOffers">
          {{ $t('detailedComparisonModal.resetOffers') }}
        </b-button>
      </div>
      <div
        class="modal-body"
        style="position: relative;"
        @mousemove="updateTooltipPosition"
      >
        <div
          v-if="tooltip.visible"
          class="hover-tooltip"
          :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        >
          {{ tooltip.text }}
        </div>

        <div
          v-for="(offer, offerIndex) in offers"
          :key="offer.id"
          class="offer-container"
        >
          <!-- Company Info Section -->
          <b-row
            class="mb-4 company-info"
            :class="{'offer-rejected': offer.rejected}"
          >
            <b-col
              md="12"
              class="company-name"
            >
              <strong>{{ offer.requestUser.company.name }}</strong>
              <small class="text-danger p-l-5">{{ offer.rejected ? "Rədd edilib" : "" }}</small>
            </b-col>
          </b-row>
          <!-- Required Conditions Section -->
          <b-row
            class="mb-4 p-3 shadow-sm bg-light rounded text-center"
            :class="{'offer-rejected': offer.rejected}"
          >
            <b-col
              v-for="(item, index) in [
                { icon: 'fa-truck', label: $t('detailedComparisonModal.catdirilma'), value: getParsedPricesExtras(offer).incoterms },
                { icon: 'fa-clock-o', label: $t('detailedComparisonModal.catdirilma2'), value: (getParsedPricesExtras(offer).deliveryDays ?? '') + ' gün' },
                { icon: 'fa-files-o', label: $t('detailedComparisonModal.paymentOption'), value: getPaymentOptionLabel(getParsedPricesExtras(offer).paymentOption) + ' ' + (getParsedPricesExtras(offer).paymentComment?? ''), md: 3 },
                { icon: 'fa-shield', label: $t('detailedComparisonModal.warrantyPeriod'), value: (getParsedPricesExtras(offer).warrantyPeriod?? '') + ' ay ' + (getParsedPricesExtras(offer).warrantyComment ?? ''), md: 3 },
                { icon: 'fa-globe', label: $t('detailedComparisonModal.manufacturerCountry'), value: getParsedPricesExtras(offer).manufacturerCountry }
              ]"
              :key="index"
              :md="item.md || 2"
              class="d-flex flex-column align-items-center p-3 border bg-white rounded shadow-sm"
            >
              <i :class="`fa ${item.icon} fa-2x text-primary mb-2`" />
              <small class="text-muted font-weight-bold">{{ item.label }}</small>
              <strong class="text-dark">{{ item.value }}</strong>
            </b-col>
          </b-row>

          <!-- Product Comparison Section -->
          <div
            v-for="(priceDetail, i) in getParsedPrices(offer)"
            :key="priceDetail.product"
            class="product-section mb-5"
            :class="{'offer-rejected': offer.rejected}"
          >
            <b-row class="product-title">
              <b-col md="12">
                <strong>{{ $t('detailedComparisonModal.productName') }}: {{ getProduct(priceDetail.product).name }}</strong>
                <br>
                <strong
                  v-if="priceDetail.alternativeOffer"
                  class="text-primary"
                >
                  {{ $t('detailedComparisonModal.nameOfAlternative') }} : {{ priceDetail.alternativeOffer.productName }}
                </strong>
              </b-col>
            </b-row>
            <!-- Product and Alternative Product Tables -->
            <b-row>
              <b-col md="6">
                <table class="table table-bordered">
                  <thead>
                    <tr>
                      <th>{{ $t('detailedComparisonModal.productPrice') }}</th>
                      <th>{{ $t('detailedComparisonModal.edv') }}</th>
                      <th>{{ $t('detailedComparisonModal.sum') }}</th>
                      <th>{{ $t('detailedComparisonModal.global') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- MAIN PRODUCT OFFER ROW -->
                    <tr
                      :class="priceDetail.isSelected ? 'table-warning' : ''"
                      style="position: relative;"
                      @mouseenter="showTooltip($t('detailedComparisonModal.selectOffer'))"
                      @mouseleave="hideTooltip"
                      @click="selectOffer(offer.id ,priceDetail.product, 'main')"
                    >
                      <td>
                        {{ priceDetail.price }} AZN x {{ getProduct(priceDetail.product).count }}
                        {{ getProduct(priceDetail.product).unit }}
                      </td>
                      <td>
                        {{
                          isVat(priceDetail)
                            ? ((priceDetail.price * 0.18) * getProduct(priceDetail.product).count).toFixed(2)
                            : 0
                        }} AZN
                      </td>
                      <td>
                        {{
                          isVat(priceDetail)
                            ? (((priceDetail.price * 0.18) + priceDetail.price) * getProduct(priceDetail.product).count).toFixed(2)
                            : priceDetail.price
                        }} AZN
                      </td>
                      <td>
                        {{ rankedOffers[offerIndex].products[i].globalRank }}
                      </td>
                    </tr>
                    <tr
                      v-if="priceDetail.alternativeOffer"
                      :class="priceDetail.alternativeOffer?.isSelected ? 'table-warning' : ''"
                      style="position: relative;"
                      @mouseenter="showTooltip($t('detailedComparisonModal.selectOffer'))"
                      @mouseleave="hideTooltip"
                      @click="selectOffer(offer.id ,priceDetail.product, 'alternative')"
                    >
                      <td class="text-primary">
                        {{ priceDetail.alternativeOffer.price }} AZN x {{ getProduct(priceDetail.product).count }}
                        {{ getProduct(priceDetail.product).unit }}
                      </td>
                      <td class="text-primary">
                        {{
                          isVat(priceDetail.alternativeOffer)
                            ? ((priceDetail.alternativeOffer.price * 0.18) * getProduct(priceDetail.product).count).toFixed(2)
                            : 0
                        }} AZN
                      </td>
                      <td class="text-primary">
                        {{
                          isVat(priceDetail.alternativeOffer)
                            ? ((priceDetail.alternativeOffer.price * 0.18) + (priceDetail.alternativeOffer.price) * getProduct(priceDetail.product).count).toFixed(2)
                            : (priceDetail.alternativeOffer.price * getProduct(priceDetail.product).count).toFixed(2)
                        }} AZN
                      </td>
                      <td>
                        {{ rankedOffers[offerIndex].products[i].alternativeOffer?.globalRank }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-col>
              <!-- Product Requirements Section -->
              <b-col md="6">
                <div class="specification-section">
                  <div>
                    <strong>{{ $t('spesifikasiyaHaqqndaQeydlr') }}</strong>
                    <p class="wrap-text">
                      {{ priceDetail.techSpecification || '-' }}
                    </p>

                    <div v-if="priceDetail.alternativeOffer">
                      <strong class="text-primary">{{$t('detailedComparisonModal.alternateSpecification')}}</strong>
                      <!-- Add the same wrap-text class here -->
                      <p class="text-primary wrap-text">
                        {{ priceDetail.alternativeOffer?.techSpecs || '-' }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-4">
                    <h5 class="fw-bold mb-2">
                      {{ $t('newAUCRequest.specialRequirements') }}
                    </h5>
                    <div class="table-responsive">
                      <table class="table table-bordered table-sm mb-0">
                        <!-- Sütun enlərini qorumaq üçün -->
                        <colgroup>
                          <col style="width: 60px;">
                          <col>
                          <col style="width: 80px;">
                        </colgroup>
                        <thead class="table-light">
                          <tr>
                            <th
                              scope="col"
                              class="text-center"
                            >
                              #
                            </th>
                            <th
                              scope="col"
                              class="text-start"
                            >
                              {{ $t('detailedComparisonModal.teleb') }}
                            </th>
                            <th
                              scope="col"
                              class="text-center"
                            >
                              {{ $t('detailedComparisonModal.cavab') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(req, idx) in getProduct(priceDetail.product).requirements"
                            :key="req.id"
                          >
                            <td class="text-center align-middle">
                              {{ idx + 1 }}
                            </td>
                            <td class="text-start align-middle">
                              {{ req.requirement }}
                            </td>
                            <td class="text-center align-middle">
                              {{ getRequirementReply(priceDetail, req.id) || '-' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <!-- Files Section -->
                <div
                  v-if="priceDetail.techSpecsFiles && priceDetail.techSpecsFiles.length"
                  class="file-preview-section mt-3"
                >
                  <strong>File-lar</strong>
                  <div class="d-flex flex-wrap mt-2">
                    <div
                      v-for="(f, ii) in priceDetail.techSpecsFiles"
                      :key="ii"
                      class="file-item mr-2 mb-2"
                    >
                      <a
                        v-if="isPhoto(f)"
                        target="_blank"
                        :href="getPhoto(f, '')"
                      >
                        <img
                          :src="getPhoto(f, 'thumbnailCropped')"
                          class="img-thumbnail"
                          style="max-height: 60px; width: 60px;"
                          :alt="$t('newRequest.requestPhoto')"
                        >
                      </a>
                      <a
                        v-else
                        target="_blank"
                        :href="getPhoto(f, '')"
                        class="file-box"
                      >
                        <div class="file-extension">
                          *.{{ f.split('.').pop() }}
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Offer Totals Section -->
          <b-row
            class="offer-summary"
            :class="{'offer-rejected': offer.rejected}"
          >
            <b-col md="4">
              <div>
                <strong>{{ $t('detailedComparisonModal.yekunQiymet') }}</strong>:
                {{ rankedOffers.at(offerIndex).mainTotal }} AZN
                <span
                  class="badge"
                  :class="{
                    'bg-primary': rankedOffers.at(offerIndex).mainRank === 1,
                    'bg-success': rankedOffers.at(offerIndex).mainRank !== 1
                  }"
                >
                  {{ rankedOffers.at(offerIndex).mainRank }}-ci yer
                </span>
              </div>
            </b-col>
            <b-col md="4">
              <div>
                <strong>{{ $t('detailedComparisonModal.alternativYekunQiymet') }}</strong>:
                {{ rankedOffers.at(offerIndex).alternativeTotal }} AZN
                <span
                  class="badge"
                  :class="{
                    'bg-primary': rankedOffers.at(offerIndex).alternativeRank === 1,
                    'bg-success': rankedOffers.at(offerIndex).alternativeRank !== 1
                  }"
                >
                  {{ rankedOffers.at(offerIndex).alternativeRank }}-ci yer
                </span>
              </div>
            </b-col>
            <b-col md="4">
              <div>
                <strong>{{ $t('detailedComparisonModal.selectedYekunQiymet') }}</strong>:
                {{ rankedOffers.at(offerIndex).selectedTotal }} AZN
              </div>
            </b-col>
          </b-row>

          <!-- Offer Validity & Action Buttons -->
          <b-row class="offer-footer mt-3">
            <b-col md="6">
              <div>{{ $t('tklifinEtibarllqMddti') }} : {{ getParsedPricesExtras(offer).valid }}</div>
            </b-col>
            <b-col
              md="6"
              class="text-right"
            >
              <b-button
                variant="primary"
                class="mr-2"
                @click="startConversation(offer.requestUser.id)"
              >
                {{ $t('mzakirEt') }}
              </b-button>

              <!-- Conditionally show Reject or Undo-Reject button -->
              <template v-if="!rankedOffers.at(offerIndex).rejected">
                <b-button
                  variant="danger"
                  @click.prevent="openRejectModal(offer.id)"
                >
                  {{ $t('detailedComparisonModal.rejectOffer') }}
                </b-button>
              </template>
              <template v-else>
                <b-button
                  style="opacity: 1;"
                  variant="warning"
                  @click.prevent="undoRejectOffer(offer.id)"
                >
                  {{ $t('detailedComparisonModal.unRejectOffer') }}
                </b-button>
                <b-button
                  class=""
                  variant="primary"
                  @click.prevent="openReason(offer.rejectReason)"
                >
                  <i class="fa fa-eye" />
                </b-button>
              </template>
            </b-col>
          </b-row>
        </div>

        <div class="row pl-5 align-end p-10">
          <b-button
            class="col-4 mr-5"
            @click="exportResultsDetailedPrice('xlsx')"
          >
            {{ $t('PQTOffersView.excelXar') }}
          </b-button>
          <b-button
            class="col-3"
            @click="exportResultsDetailedPrice('pdf')"
          >
            {{ $t('PQTOffersView.pdfXar') }}
          </b-button>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeModal"
        >
          {{ $t('tmm') }}
        </b-button>
        <b-button
          variant="primary"
          @click="confirm"
        >
          {{ $t('companyProfile.lvEt') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Custom Reject Offer Modal -->
    <b-modal
      id="reject-offer-modal"
      v-model="isRejectModalVisible"
      :title="$t('detailedComparisonModal.question')"
      :ok-title="$t('detailedComparisonModal.rejectOffer')"
      :cancel-title="$t('companyProfile.lvEt')"
      ok-variant="danger"
      @ok="confirmRejectOffer"
    >
      <b-form-group :label="$t('detailedComparisonModal.rejectReason')">
        <b-form-textarea
          v-model="rejectReason"
          :placeholder="$t('detailedComparisonModal.writeReason')"
          rows="5"
        />
      </b-form-group>
      <b-alert
        v-if="rejectValidationMessage"
        variant="danger"
        dismissible
      >
        {{ rejectValidationMessage }}
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import {
  getPaymentOptionLabel,
  rejectOffer,
  resetSelectedOffer,
  selectOffer,
  unRejectOffer
} from "@/repositories/GeneralDataRepository";
import {mapState} from "vuex";

export default {
  name: "DetailedComparisonModal",
  props: {
    visible: Boolean,
    request_id: Number,
    rankedOffers: Array,
    offers: Array,
    getParsedPrices: Function,
    getProduct: Function,
    getRequirementReply: Function,
    getTechSpecification: Function,
    getParsedPricesExtras: Function,
    startConversation: Function,
    exportResultsDetailedPrice: Function,
    isPhoto: Function,
    getPhoto: Function
  },
  data() {
    return {
      tooltip: {
        visible: false,
        text: '',
        x: 0,
        y: 0
      },
      isVisible: this.visible,
      // For the custom reject modal:
      isRejectModalVisible: false,
      rejectOfferId: null,
      rejectReason: "",
      rejectValidationMessage: ""
    };
  },
  computed: {
    ...mapState({
      token: state => state.auth.token,
      user: state => state.auth.user,
      company: state => state.auth.company,
      profile: state => state.auth.profile,
      sessionType: state => state.auth.sessionType,
      position: state => state.auth.position,
    }),
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
    },
    isVisible(newValue) {
      this.$emit("update:visible", newValue);
    },
  },
  methods: {
    // Continuously update tooltip position
    updateTooltipPosition(event) {
      // If .modal-body is position: relative, we can measure relative offsets:
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltip.x = event.clientX - rect.left;
      this.tooltip.y = event.clientY - rect.top;
    },
    // Show tooltip with given text
    showTooltip(text) {
      this.tooltip.visible = true;
      this.tooltip.text = text;
    },
    // Hide tooltip
    hideTooltip() {
      this.tooltip.visible = false;
    },
    // Method to call when an offer is clicked
    selectOffer(offerId, productId, type) {
      selectOffer(this.token, offerId, productId, type === 'alternative').then(() => {
        this.$emit("refreshData");
      }).catch(error => {
        console.error("Error rejecting offer:", error);
        this.$bvToast.toast("Something went wrong", {variant: "danger"});
      });
    },
    resetOffers() {
      resetSelectedOffer(this.token, this.request_id).then(() => {
        this.$swal({icon: 'success', title: "Uğurla yeniləndi"});
        this.$emit("refreshData");
      }).catch(error => {
        console.error("Error rejecting offer:", error);
        this.$bvToast.toast("Something went wrong", {variant: "danger"});
      });
    },
    openReason(reason) {
      this.$swal({
        title: "Rədd səbəbi",
        text: reason,
        icon: 'warning'

      })
    },
    getPaymentOptionLabel,
    openRejectModal(offerId) {
      this.rejectOfferId = offerId;
      this.rejectReason = "";
      this.rejectValidationMessage = "";
      this.isRejectModalVisible = true;
    },
    confirmRejectOffer(bvModalEvt) {
      if (!this.rejectReason || !this.rejectReason.trim()) {
        this.rejectValidationMessage = "Zəhmət olmasa səbəb yazın!";
        bvModalEvt.preventDefault();
        return;
      }
      rejectOffer(this.token, this.rejectOfferId, this.rejectReason)
          .then(() => {
                this.$bvToast.toast("Rədd edildi", {variant: "success"});
                this.$emit("refreshData");
              }
          )
          .catch(error => {
            console.error("Error rejecting offer:", error);
            this.$bvToast.toast("Something went wrong", {variant: "danger"});
          });
    },
    undoRejectOffer(offerId) {
      // Call your API to undo rejection
      unRejectOffer(this.token, offerId)
          .then(() => {
            this.$bvToast.toast("Rədd etmə əməliyyatı geri götürüldü", {variant: "success"});
            this.$emit("refreshData")
          })
          .catch(error => {
            console.error("Error undoing rejection:", error);
            this.$bvToast.toast("Something went wrong", {variant: "danger"});
          });
    },
    isVat(offer) {
      return offer.isVatPayer === 'true';
    },
    closeModal() {
      this.isVisible = false;
    },
    confirm() {
      this.$emit("confirm");
      this.isVisible = false;
    }
  }
};
</script>

<style scoped>
.offer-container {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.offer-rejected {
  background-color: #f9f9f9; /* Light gray background */
  opacity: 0.6; /* Make it look 'disabled' */
}


.condition-item .icon-box {
  font-size: 1.5rem;
  color: #007bff;
  margin-bottom: 8px;
}

.condition-item .label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.condition-item .value {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
}

.offer-container {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  background-color: #f8f9fa;
}

.company-info .company-name {
  font-size: 1.5rem;
  font-weight: bold;
}

.required-conditions div {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #e9ecef;
  text-align: center;
  font-size: 0.9rem;
}

.table-bordered th,
.table-bordered td {
  text-align: center;
  vertical-align: middle;
}

.specification-section p {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px 12px;
  background-color: #f1f1f1;
  min-height: 50px;
}

.offer-summary div {
  font-size: 1.2rem;
  font-weight: bold;
}

.badge.bg-primary {
  background-color: #007bff;
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
}

.offer-footer {
  border-top: 1px solid #dee2e6;
  padding-top: 16px;
}

.offer-footer .text-right {
  text-align: right;
}
</style>
<style scoped>
.hover-tooltip {
  position: absolute;
  font-size: 10px;
  transform: translate(-50%, -50%);
  background-color: #007bff; /* Bootstrap primary */
  color: #fff;
  border-radius: 50%;
  width: 50px; /* Adjust as needed */
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none; /* Let clicks pass through */
  font-weight: bold;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 9999; /* Add this line */
}

.wrap-text {
  /* Forces long words or unbroken text to wrap to the next line */
  white-space: pre-wrap; /* Preserve whitespace & wraps long words */
  word-wrap: break-word; /* Legacy name for forcing breaks */
  overflow-wrap: break-word; /* Standard name for forcing breaks */
}
</style>